import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { X, Trophy, Sparkles, Star, Rocket } from 'lucide-react';
import { Button } from '../../../../components/ui/button';

const CONGRATULATION_MESSAGES = [
  {
    title: 'You Did It!',
    emoji: '🎉',
    message:
      'Your dedication and hard work have paid off! This promotion is a testament to your ' +
      "incredible skills and commitment. Keep pushing boundaries - the sky's the limit!",
    closing: 'Celebrate this moment!',
  },
  {
    title: 'Level Up!',
    emoji: '🚀',
    message:
      "You've unlocked a new achievement in your career journey! Your persistence and " +
      'talent have brought you here. This is just the beginning of even greater things.',
    closing: 'Keep soaring high!',
  },
  {
    title: 'Brilliant Achievement!',
    emoji: '⭐',
    message:
      'Stars like you make our team shine! Your growth mindset and exceptional performance ' +
      "have earned you this well-deserved promotion. We're proud to have you on the team!",
    closing: 'Shine on!',
  },
  {
    title: 'Outstanding Progress!',
    emoji: '🏆',
    message:
      "Champions are made through dedication, and you've proven yourself to be one! Your " +
      'journey to this promotion shows your remarkable ability to learn, adapt, and excel.',
    closing: 'You earned it!',
  },
  {
    title: 'New Heights Reached!',
    emoji: '🎯',
    message:
      'You aimed high and hit your target! This promotion reflects your unwavering focus ' +
      'and determination. Your growth inspires everyone around you. Onwards and upwards!',
    closing: 'Target achieved!',
  },
  {
    title: 'Superstar Alert!',
    emoji: '💫',
    message:
      'Your talent and hard work have been recognized! This promotion is just one milestone ' +
      'in what we know will be an extraordinary career. Dream big and keep growing!',
    closing: 'The future is bright!',
  },
  {
    title: 'Well Deserved!',
    emoji: '🌟',
    message:
      "Every step you've taken has led to this moment. Your commitment to excellence and " +
      'continuous improvement makes you a valuable team member. Congratulations!',
    closing: 'Well done!',
  },
  {
    title: 'Victory Lap!',
    emoji: '🥇',
    message:
      "You've crossed the finish line and earned gold! Your relentless pursuit of growth " +
      "and excellence has paid off. Celebrate - you've earned every bit of this success!",
    closing: 'Champion status!',
  },
  {
    title: 'Rising Star!',
    emoji: '✨',
    message:
      "Your potential is limitless, and this promotion proves it! You've shown incredible " +
      'growth and resilience. Keep that momentum going - amazing things await!',
    closing: 'Keep rising!',
  },
  {
    title: 'Mission Accomplished!',
    emoji: '🎊',
    message:
      'You set your sights on growth and delivered! This promotion celebrates your skills, ' +
      "dedication, and the positive impact you bring every day. Here's to many more!",
    closing: 'Mission complete!',
  },
];

const ICONS = [Trophy, Sparkles, Star, Rocket];

function CongratulatePopUp({ open, handleClose }) {
  const randomMessage = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * CONGRATULATION_MESSAGES.length);
    return CONGRATULATION_MESSAGES[randomIndex];
  }, [open]);

  const RandomIcon = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * ICONS.length);
    return ICONS[randomIndex];
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop with confetti-like gradient */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-black/50 to-blue-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative bg-background rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in zoom-in-95 duration-300"
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        {/* Decorative top gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-primary/20 to-transparent" />

        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="relative px-6 pt-8 pb-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg">
              <RandomIcon className="h-10 w-10 text-white" />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
              {randomMessage.title}
              <span className="text-3xl">{randomMessage.emoji}</span>
            </h2>
            <p className="text-primary font-medium mt-1">Congratulations on your promotion!</p>
          </div>

          {/* Message */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6">
            <p className="text-muted-foreground text-center leading-relaxed">
              {randomMessage.message}
            </p>
          </div>

          {/* Closing */}
          <p className="text-center text-sm text-muted-foreground mb-6">
            {randomMessage.closing}
            <br />
            <span className="font-medium text-foreground">— Onix-Systems Team</span>
          </p>

          {/* Action Button */}
          <Button
            onClick={handleClose}
            className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 text-white font-medium py-6"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Celebrate!
          </Button>
        </div>

        {/* Decorative bottom elements */}
        <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-primary/10 to-transparent rounded-tr-full" />
        <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-purple-500/10 to-transparent rounded-tl-full" />
      </div>
    </div>
  );
}

CongratulatePopUp.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default CongratulatePopUp;
